import * as React from 'react';
import { Map } from 'mapbox-gl';

import { ILayer } from '../../types/ILayer';
import { IStructure } from '../../types/IStructure';
import { IPoint } from '../../types/IPoint';
import { IReference } from '../../types/IReference';
import { IProject } from '../../types/IProject';
import { IRect } from '../../types/IRect';
import { IArea } from '../../types/IArea';
import { IDriverset, ISystemLayer } from '../../api/models';
import { ISpecies } from '../../api/models/ISpecies';
import { TResourceState } from '../../types/TResourceState';

interface IOrganixRun {
  area: IArea;
  // 'simulate' runs the grid + invokes the ORGANIX (and optionally Diagenix)
  // API. 'export' runs the grid only, then downloads the data and unmounts.
  intent: 'simulate' | 'export';
}

type TMode =
    null
  | 'account'         // Account pane open
  | 'project'         // Project pane open
  | 'layers'          // Layers pane open
  | 'structures'      // Structures pane open
  | 'references'      // References pane open
  | 'areas'           // Areas pane open
  | 'systems'         // Systems pane open
  | 'species'         // Species pane open
  | 'users'           // Users pane open
  | 'info'            // Info dialog open
  | 'circle-builder'  // Circle builder
  | 'polygon-builder' // Polygon builder
  | 'area-builder'    // Area builder
  | 'area-editor'     // Area editor
  | 'box-select'      // Box select for multi-structure selection
  | 'eyedropper'      // Eyedropper tool for layer value inspection
  ;

interface IGlobalState {
  mode: TMode,
  setMode: (mode: TMode) => void;
  getMap: () => Map | null;
  setMap: (map: Map) => void;
  expanded: boolean,
  setPaneExpansion: (expanded: boolean) => void;
  togglePaneExpansion: () => void;
  guide: boolean;
  setGuide: (guide: boolean) => void;

  // Project
  project: IProject;
  setProject: (project: IProject) => void;
  startLoadProject: () => void;
  loadProject: (json: any) => void;
  parseLayers: (systemLayers: ISystemLayer[]) => void;
  clearProject: () => void;
  saveProject: () => void;

  // Layers
  layers: ILayer[];
  addLayers: (newLayers: ILayer[], clear?: boolean) => void;
  /** Add simulation output, stamped as a new run; hides earlier results. */
  addResultLayers: (newLayers: ILayer[], areaName: string) => void;
  /** Remove every layer belonging to one run. */
  deleteRun: (runId: string) => void;
  startImport: () => void;
  toggleLayer: (layer: ILayer) => void;
  activeLayer: ILayer | null,
  setActiveLayer: (layer: ILayer | null) => void;
  deleteLayer: (layer: ILayer) => void;
  focusLayer: (layer: ILayer) => void;
  updateLayer: (layer: ILayer) => void;
  clearLayers: () => void;

  // Structures:
  structures: IStructure[];
  showStructures: boolean;
  setShowStructures: (value: boolean) => void;
  setStructures: (structures: IStructure[]) => void;
  clearStructures: () => void;
  addStructure: (structure: IStructure) => void;
  addStructureCircle: (point: IPoint, radius: number) => void;
  addStructurePolygon: (points: IPoint[]) => void;
  focusStructure: (structure: IStructure) => void;
  startEditStructure: () => void;

  // Multi-select structures:
  selectedStructures: IStructure[];
  setSelectedStructures: (structures: IStructure[]) => void;
  toggleStructureSelection: (structure: IStructure, additive: boolean) => void;
  selectAllStructures: () => void;
  clearStructureSelection: () => void;
  deleteSelectedStructures: () => void;
  moveSelectedStructures: (dLng: number, dLat: number) => void;
  updateSelectedStructureCircle: (point: IPoint, radius: number) => void;
  updateSelectedStructurePolygon: (points: IPoint[]) => void;

  // References:
  references: IReference[];
  setReferences: (references: IReference[]) => void;
  clearReferences: () => void;
  startReferenceImport: () => void;
  selectedReference: IReference | null;
  setSelectedReference: (reference: IReference | null) => void;
  toggleReference: (reference: IReference) => void;
  updateReference: (reference: IReference) => void;
  deleteReference: (reference: IReference) => void;
  focusReference: (reference: IReference) => void;

  // Areas
  areas: IArea[];
  setAreas: (areas: IArea[]) => void;
  selectedArea: IArea | null;
  setSelectedArea: (area: IArea | null) => void;
  deleteArea: (area: IArea) => void;
  clearAreas: () => void;
  addArea: (rect: IRect) => void;
  updateSelectedArea: (newRect: IRect) => void;
  updateArea: (area: IArea) => void;
  focusArea: (area: IArea) => void;
  startOrganix: (area: IArea) => void;
  startOrganixExport: (area: IArea) => void;
  stopOrganix: () => void;
  organix: IOrganixRun | null;
  hasBathymetry: () => boolean;
  hasCurrentSpeed: () => boolean;

  // Driver sets belonging to the currently-loaded system (read-only mirror).
  // Empty when no system is loaded. Each area carries its own driverset,
  // copied from this list when the area is created.
  driversets: IDriverset[];
  setDriversets: (driversets: IDriverset[]) => void;

  // Species:
  speciesReady: TResourceState;
  species: ISpecies[];
  loadSpecies: () => void;
  createSpecies: (species: ISpecies) => Promise<ISpecies>;
  updateSpecies: (species: ISpecies, values: ISpecies) => Promise<boolean>;
  deleteSpecies: (species: ISpecies) => Promise<boolean>;  
}

const GlobalStateContext = React.createContext<IGlobalState|null>(null);

const useGlobalState = (): IGlobalState => {
  const ctx = React.useContext(GlobalStateContext);
  if (!ctx) throw new Error('useGlobalState must be used inside a <GlobalState> provider.');
  return ctx;
}

export { IGlobalState, GlobalStateContext, useGlobalState, TMode, IOrganixRun }
